// @ts-check
// Estatísticas do bot (/service/bots/:botId/stats?from&to&view=nodes|runs; contrato de bots, seções 18 e 22.3): período
// (Hoje, 7 dias, 30 dias ou personalizado), seis números (iniciadas, concluídas sem pessoa, transferidas, sem resposta,
// paradas, atendimentos abertos) com as taxas, barras por dia, a aba Blocos (canvas somente leitura com os números de
// cada bloco e de cada ramo, motivos de fim e inválidas por pergunta) e a aba Execuções.
import { html } from 'htm/preact';
import { useEffect, useMemo, useRef, useState } from 'preact/hooks';
import { navigate, searchParamsState, updateSearchParams } from '../../app/router.js';
import { ApiError } from '../../data/api-client.js';
import { getBot, getBotNodeCounts, getBotStats } from '../../data/bots.js';
import { Button } from '../../ui/button.js';
import { EmptyState, Skeleton } from '../../ui/feedback.js';
import { Icon } from '../../ui/icon.js';
import { SegmentedControl } from '../../ui/segmented-control.js';
import { TabList } from '../../ui/tab-list.js';
import { formatNumber, getFormatPreferences } from '../../lib/format.js';
import { BOT_LIST_PATH, botEditorHref } from '../../lib/bot-list.js';
import { BOT_STATS_PRESETS, botRates, botStatsRange, invalidAnswerRate, readBotStatsPeriod, sortedEndReasons } from '../../lib/bot-stats.js';
import { botStepNumbers } from '../../lib/bot-editor-state.js';
import { botProblemsByNode } from '../../lib/bot-validation.js';
import { walkBotTree } from '../../lib/bot-tree.js';
import { PageCard, PageHeader } from '../page/page-card.js';
import { FlowMenuButton } from '../flows/flow-menu.js';
import { BotCanvas } from './bot-canvas.js';
import { BotRunsList } from './bot-runs-list.js';
import { useBotLookupData, useBotSummaryLookups } from './bot-lookups.js';
import { BotStatusChip, botReasonText, describeBotNode } from './bot-ui.js';
import { botRateText } from './bots-page.js';
import { t } from '../../i18n/index.js';

/**
 * @typedef {import('../../data/bots.js').BotDetail} BotDetail
 * @typedef {import('../../data/bots.js').BotNodeCounts} BotNodeCounts
 * @typedef {import('../../lib/bot-stats.js').BotStats} BotStats
 */

/** @param {{ botId: string }} props */
export function BotStatsPage({ botId }) {
  const parameters = searchParamsState.value;
  const timeZone = getFormatPreferences().timeZone;
  const period = readBotStatsPeriod(parameters, new Date(), timeZone);
  const [bot, setBot] = useState(/** @type {BotDetail | null} */ (null));
  const [loadState, setLoadState] = useState(/** @type {'loading' | 'ready' | 'error' | 'notFound'} */ ('loading'));
  const [stats, setStats] = useState(/** @type {BotStats | null} */ (null));
  const [statsError, setStatsError] = useState(false);
  const [live, setLive] = useState(/** @type {BotNodeCounts | null} */ (null));
  const [custom, setCustom] = useState({ from: period.from, to: period.to });
  const [isCustomOpen, setIsCustomOpen] = useState(period.preset === 'CUSTOM');
  const [focusTarget, setFocusTarget] = useState('entry');
  const [zoom, setZoom] = useState(0.8);
  const lookupData = useBotLookupData();
  const lookups = useBotSummaryLookups(lookupData, bot?.definition ?? null);

  const loadBot = () => {
    setLoadState('loading');
    getBot(botId).then((loaded) => {
      setBot(loaded);
      setLoadState('ready');
      document.title = `${loaded.name} · ${t('bots.stats.title')}`;
    }).catch((error) => setLoadState(error instanceof ApiError && (error.statusCode === 404 || error.code === 'RECORD_NOT_FOUND') ? 'notFound' : 'error'));
  };
  useEffect(loadBot, [botId]);
  const loadStats = () => {
    setStatsError(false);
    setStats(null);
    getBotStats(botId, { from: period.from, to: period.to }).then(setStats).catch(() => setStatsError(true));
    getBotNodeCounts(botId).then(setLive).catch(() => undefined);
  };
  useEffect(loadStats, [botId, period.from, period.to]);
  const stepNumbers = useMemo(() => (bot ? botStepNumbers(bot.definition) : new Map()), [bot]);

  const simpleHeader = html`<${PageHeader} icon="IconChartBar" title=${t('bots.stats.title')} />`;
  if (loadState === 'notFound' || loadState === 'error') {
    return html`<${PageCard} className="flows-page bots-page" header=${simpleHeader}>
      <div class="flows-state"><${EmptyState} icon="IconAlertTriangle" title=${loadState === 'notFound' ? t('bots.editor.notFound') : t('bots.stats.loadError')}
        action=${html`<${Button} title=${t('bots.editor.backToList')} variant="secondary" size="small" onClick=${() => navigate(BOT_LIST_PATH)} />`} /></div>
    <//>`;
  }
  if (!bot) {
    return html`<${PageCard} className="flows-page bots-page" header=${simpleHeader}>
      <div class="flow-stats" aria-busy="true" data-testid="bot-stats-loading"><${Skeleton} height=${32} width="40%" />
        <div class="flow-stats-numbers">${[0, 1, 2, 3, 4, 5].map((key) => html`<${Skeleton} key=${key} height=${88} radius=${8} />`)}</div></div>
    <//>`;
  }

  const totals = stats?.totals ?? null;
  const rates = botRates(totals);
  const numberCards = [
    { key: 'started', icon: 'IconPlayerPlay', value: totals?.started, rate: null },
    { key: 'completed', icon: 'IconCircleCheck', value: totals?.completed, rate: rates.completedWithoutHuman },
    { key: 'handedOff', icon: 'IconHeadset', value: totals?.handedOff, rate: rates.handedOff },
    { key: 'timedOut', icon: 'IconClock', value: totals?.timedOut, rate: rates.abandoned },
    { key: 'stopped', icon: 'IconPlayerStop', value: totals?.stopped, rate: null },
    { key: 'ticketsOpened', icon: 'IconTicket', value: totals?.ticketsOpened, rate: null },
  ];
  const view = period.view;
  /** @type {Array<{ id: string, title: string, rate: number | null, invalid: number }>} */
  const questions = [];
  walkBotTree(bot.definition.nodes, (node) => {
    if (node.type === 'ASK' || (node.type === 'SEND_MESSAGE' && node.config?.options?.source)) {
      const nodeStats = stats?.nodes?.[node.id];
      questions.push({ id: node.id, title: `${stepNumbers.get(node.id) ?? ''}. ${describeBotNode(node, lookups).title}`, rate: invalidAnswerRate(nodeStats), invalid: Number(nodeStats?.invalid ?? 0) });
    }
  });
  const menuItems = [
    { key: 'test', text: t('bots.list.menu.test'), icon: 'IconMessageCircle', onSelect: () => navigate(botEditorHref(bot.id, { test: true })) },
  ];
  const header = html`<${PageHeader} icon="IconChartBar" title=${bot.name}
    breadcrumb=${html`<button type="button" class="flow-back-link" onClick=${() => navigate(BOT_LIST_PATH)}><${Icon} name="IconChevronLeft" size=${16} /><span>${t('bots.list.title')}</span></button>`}
    actions=${html`<${BotStatusChip} status=${bot.status} inactiveReason=${bot.inactiveReason} />
      <div class="flow-split-button">
        <${Button} title=${t('bots.stats.edit')} icon="IconPencil" accent="blue" size="small" onClick=${() => navigate(botEditorHref(bot.id))} />
        <${FlowMenuButton} ariaLabel=${t('bots.stats.moreActions')} icon="IconChevronDown" items=${menuItems} className="flow-menu-trigger flow-split-trigger" />
      </div>`} />`;
  return html`<${PageCard} className="flows-page bots-page bot-stats-page" header=${header}>
    <div class="flow-stats" data-testid="bot-stats-page">
      <div class="flow-stats-period">
        <${SegmentedControl} label=${t('bots.stats.period')} value=${isCustomOpen ? 'CUSTOM' : period.preset}
          options=${BOT_STATS_PRESETS.map((preset) => ({ value: preset, label: t(`bots.stats.presets.${preset}`) }))}
          onChange=${(/** @type {string} */ preset) => {
            if (preset === 'CUSTOM') {
              setIsCustomOpen(true);
              return;
            }
            setIsCustomOpen(false);
            const range = botStatsRange(preset, new Date(), timeZone);
            updateSearchParams({ from: range.from, to: range.to });
          }} />
        ${isCustomOpen
          ? html`<form class="flow-stats-custom" onSubmit=${(/** @type {Event} */ event) => {
              event.preventDefault();
              if (custom.from && custom.to && custom.from <= custom.to) updateSearchParams({ from: custom.from, to: custom.to });
            }}>
              <input type="date" class="text-input" value=${custom.from} aria-label=${t('bots.stats.from')}
                onInput=${(/** @type {InputEvent} */ event) => setCustom({ ...custom, from: /** @type {HTMLInputElement} */ (event.currentTarget).value })} />
              <input type="date" class="text-input" value=${custom.to} aria-label=${t('bots.stats.to')}
                onInput=${(/** @type {InputEvent} */ event) => setCustom({ ...custom, to: /** @type {HTMLInputElement} */ (event.currentTarget).value })} />
              <${Button} title=${t('bots.stats.apply')} variant="secondary" size="small" type="submit" disabled=${!custom.from || !custom.to || custom.from > custom.to} />
            </form>`
          : null}
      </div>
      ${statsError
        ? html`<${EmptyState} icon="IconAlertTriangle" title=${t('bots.stats.loadError')}
            action=${html`<${Button} title=${t('bots.common.retry')} variant="secondary" size="small" onClick=${loadStats} />`} />`
        : html`<div class="flow-stats-numbers">
            ${numberCards.map((card) => html`<div key=${card.key} class="flow-stats-number" data-testid=${`bot-stats-${card.key}`}>
              <span class="flow-stats-number-label"><${Icon} name=${card.icon} size=${14} />${t(`bots.stats.numbers.${card.key}`)}</span>
              ${stats === null
                ? html`<${Skeleton} width=${64} height=${28} />`
                : html`<span class="flow-stats-number-value">${formatNumber(Number(card.value ?? 0))}</span>
                  ${card.rate !== null ? html`<span class="flow-stats-rate">${botRateText(card.rate)}</span>` : null}`}
            </div>`)}
          </div>`}
      <${BotStatsChart} series=${stats?.series ?? null} />
      <${TabList} activeTabId=${view} onChange=${(/** @type {string} */ tab) => updateSearchParams({ view: tab === 'nodes' ? null : tab })}
        tabs=${[{ id: 'nodes', title: t('bots.stats.tabs.nodes') }, { id: 'runs', title: t('bots.stats.tabs.runs') }]} />
      <div class="flow-stats-tab" role="tabpanel">
        ${view === 'nodes'
          ? html`${stats && Number(totals?.started ?? 0) === 0 ? html`<p class="flow-stats-empty" data-testid="bot-stats-empty">${t('bots.stats.noData')}</p>` : null}
            <div class="flow-stats-canvas bot-stats-canvas">
              <${BotCanvas} definition=${bot.definition} lookups=${lookups} lookupData=${lookupData} problems=${[]} problemsByNode=${botProblemsByNode([])}
                stepNumbers=${stepNumbers} selectedNodeId=${null} focusTarget=${focusTarget} onFocusTarget=${setFocusTarget} zoom=${zoom} onZoom=${setZoom}
                showCounts=${true} canShowCounts=${false} onToggleCounts=${() => undefined} counts=${live} stats=${stats} isReadOnly
                ariaLabel=${t('bots.canvas.treeLabel', { name: bot.name })} />
            </div>
            <div class="flow-stats-exits">
              <section class="flow-stats-reasons">
                <h3 class="flow-stats-section-title">${t('bots.stats.endReasons')}</h3>
                ${sortedEndReasons(stats?.endReasons).length === 0
                  ? html`<p class="flow-field-hint">${t('bots.stats.noneInPeriod')}</p>`
                  : html`<ul>${sortedEndReasons(stats?.endReasons).map((entry) => html`<li key=${entry.reason}><span>${botReasonText('END', entry.reason)}</span><strong>${formatNumber(entry.count)}</strong></li>`)}</ul>`}
              </section>
              <section class="flow-stats-reasons">
                <h3 class="flow-stats-section-title">${t('bots.stats.invalidAnswers')}</h3>
                ${questions.length === 0
                  ? html`<p class="flow-field-hint">${t('bots.stats.noQuestions')}</p>`
                  : html`<ul>${questions.map((question) => html`<li key=${question.id}><span>${question.title}</span>
                      <strong title=${t('bots.stats.invalidCount', { count: question.invalid })}>${botRateText(question.rate)}</strong></li>`)}</ul>`}
              </section>
            </div>`
          : html`<${BotRunsList} botId=${bot.id} definition=${bot.definition} lookups=${lookups} />`}
      </div>
    </div>
  <//>`;
}

/**
 * Iniciadas e transferidas por dia, com a tabela para leitor de tela.
 * @param {{ series: BotStats['series'] | null }} props
 */
function BotStatsChart({ series }) {
  const barsRef = useRef(/** @type {HTMLDivElement | null} */ (null));
  // no celular os dias não cabem e as barras rolam de lado: o gráfico abre nos dias mais recentes, à direita
  useEffect(() => {
    const bars = barsRef.current;
    if (bars && bars.scrollWidth > bars.clientWidth) bars.scrollLeft = bars.scrollWidth;
  }, [series]);
  if (series === null) return html`<${Skeleton} height=${160} radius=${8} />`;
  const maximum = Math.max(1, ...series.map((item) => item.started));
  /** @param {string} bucket */
  const label = (bucket) => {
    const [, month, day] = bucket.split('-');
    return `${day}/${month}`;
  };
  return html`<figure class="flow-stats-chart bot-stats-chart" aria-label=${t('bots.stats.chartLabel')}>
    <div class="flow-stats-legend" aria-hidden="true">
      <span data-series="entered">${t('bots.stats.numbers.started')}</span>
      <span data-series="completed">${t('bots.stats.numbers.handedOff')}</span>
    </div>
    <div class="flow-stats-bars" aria-hidden="true" ref=${barsRef}>
      ${series.map((item) => html`<div key=${item.bucket} class="flow-stats-bar-group" title=${`${label(item.bucket)}: ${formatNumber(item.started)} / ${formatNumber(item.handedOff)}`}>
        <div class="flow-stats-bar-pair">
          <span class="flow-stats-bar" data-series="entered" style=${{ height: `${(item.started / maximum) * 100}%` }}></span>
          <span class="flow-stats-bar" data-series="completed" style=${{ height: `${(item.handedOff / maximum) * 100}%` }}></span>
        </div>
        <span class="flow-stats-bar-label">${label(item.bucket)}</span>
      </div>`)}
    </div>
    <table class="visually-hidden-text">
      <caption>${t('bots.stats.chartLabel')}</caption>
      <thead><tr><th scope="col">${t('bots.stats.day')}</th><th scope="col">${t('bots.stats.numbers.started')}</th><th scope="col">${t('bots.stats.numbers.handedOff')}</th></tr></thead>
      <tbody>${series.map((item) => html`<tr key=${item.bucket}><td>${label(item.bucket)}</td><td>${item.started}</td><td>${item.handedOff}</td></tr>`)}</tbody>
    </table>
  </figure>`;
}
